import {
  Button,
  Divider,
  Typography,
  Switch,
  Tooltip,
  styled,
} from '@mui/material';
import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm } from 'react-hook-form';
import FullPageDrawer from 'components/new/FullPageDrawer';
import { useLayoutContext } from 'contexts/LayoutContext';
import { Column, Row } from 'further-ui/components/Layout';
import FileDrop from 'further-ui/components/FileDrop';
import useFileUpload from 'hooks/ui/useFileUpload';
import { UploadType } from 'further-types/files';
import BasicSelect from 'further-ui/components/BasicSelect';
import { InfoOutline } from '@mui/icons-material';
import { vehicleSigningDocument as rules } from 'further-ui/validations';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import api from 'lib/trpcClient';
import {
  getValidFileTypesForUploadType,
  getMaxSizeForUploadType,
} from 'further-ui/config';

const Content = styled(Column)({
  maxWidth: 500,
  height: '100%',
  maxHeight: '100%',
});

type Props = {
  open: boolean;
  onClose: () => void;
  onDocumentAdded: (vehicleSigningDocumentId: string) => void;
};

type FormValues = {
  documentFile:
    | {
        uri: string;
        objectKey: string;
        fileName: string;
      }
    | undefined;
  maxUniqueSigners: number;
  requireWitnessing: boolean;
};

const AddDocument: React.FC<Props> = ({ open, onClose, onDocumentAdded }) => {
  const { vehicle } = useLayoutContext();
  const { loading: isUploading, uploadFile } = useFileUpload();
  const { control, handleSubmit } = useForm<FormValues>({
    resolver: zodResolver(rules.AddSigningDocumentInput),
    defaultValues: {
      documentFile: undefined,
      maxUniqueSigners: 1,
      requireWitnessing: false,
    },
  });

  const { mutate: addDocument, isPending: isAddingDocument } =
    api.vehicleSigningDocument.create.useMutation(
      useHandleTrpcMutation<{ vehicleSigningDocumentId: string }>()({
        successMessage: 'Document added successfully.',
        // don't invalidate here because we want to open the edit signature locations drawer
        // first, plus the mutation called after this will invalidate the list documents query
        invalidations: [],
        onSuccess: (data) => {
          // close this drawer and open the edit signature locations drawer
          onClose();
          onDocumentAdded(data.vehicleSigningDocumentId);
        },
      }),
    );

  if (!open || !vehicle) return null;

  const onSubmit = (data: FormValues) => {
    addDocument({
      documentFile: {
        uri: data.documentFile?.uri ?? '',
        objectKey: data.documentFile?.objectKey ?? '',
        fileName: data.documentFile?.fileName ?? '',
      },
      maxUniqueSigners: data.maxUniqueSigners,
      requireWitnessing: data.requireWitnessing,
      vehicleId: vehicle._id,
    });
  };

  return (
    <FullPageDrawer
      open={true}
      onClose={onClose}
      title="Add new document"
      actions={
        <Row centered spacing="sm" justify="space-between">
          <Button
            variant="outlined"
            color="primary"
            size="large"
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            size="large"
            loading={isAddingDocument}
            onClick={handleSubmit(onSubmit)}
          >
            Select signature location(s)
          </Button>
        </Row>
      }
    >
      <Content spacing="xl" fullWidth justify="center">
        <form>
          <Column spacing="lg">
            <Controller
              name="documentFile"
              control={control}
              render={({
                field: { value, onChange },
                fieldState: { error },
              }) => {
                return (
                  <FileDrop
                    inputTitle="Upload new document"
                    maxFiles={1}
                    files={value ? [{ ...value, isUploading }] : []}
                    onDeleteFile={() => {
                      onChange(undefined);
                    }}
                    onDropFile={(files) => {
                      const droppedFile = files[0];

                      onChange({
                        uri: droppedFile.previewUri,
                        objectKey: '',
                        fileName: droppedFile.fileName,
                      });

                      const handleFileUpload = async (file: File) => {
                        const { data } = await uploadFile(
                          file,
                          UploadType.VehicleSigningDocuments,
                        );
                        if (!data) return;

                        onChange({
                          uri: data.url,
                          objectKey: data.filePath,
                          fileName: droppedFile.fileName,
                        });
                      };

                      void handleFileUpload(droppedFile.fileData);
                    }}
                    sizeRestriction={{
                      unit: 'mb',
                      value: getMaxSizeForUploadType(
                        UploadType.VehicleSigningDocuments,
                        'mb',
                      ),
                    }}
                    supportedFormats={getValidFileTypesForUploadType(
                      UploadType.VehicleSigningDocuments,
                    )}
                    error={!!error?.message}
                    helperText={error?.message}
                  />
                );
              }}
            />
            <Divider />
            <Row centered fullWidth spacing="sm" justify="space-between">
              <Row centered spacing="xxs">
                <Typography variant="h6">
                  Maximum unique signatories per investor
                </Typography>
                <Tooltip
                  title={
                    <>
                      Select the number of potential signatories that may sign
                      the document. Given different jurisdictional requirements,
                      for an entity this is typically 2, with 1 always required.{' '}
                      <strong>Note:</strong> This is not how many times a
                      signatory signs, but how many unique signatories there may
                      be.
                    </>
                  }
                >
                  <InfoOutline fontSize="inherit" />
                </Tooltip>
              </Row>
              <Controller
                name="maxUniqueSigners"
                control={control}
                render={({ field }) => {
                  return (
                    <BasicSelect
                      {...field}
                      options={Array.from({ length: 4 }, (_, index) => ({
                        value: index + 1,
                        label: (index + 1).toString(),
                      }))}
                    />
                  );
                }}
              />
            </Row>
            <Divider />
            <Row centered fullWidth spacing="sm" justify="space-between">
              <Typography variant="h6">
                Signatories require witnesses
              </Typography>
              <Controller
                name="requireWitnessing"
                control={control}
                render={({ field }) => {
                  return <Switch {...field} />;
                }}
              />
            </Row>
          </Column>
        </form>
      </Content>
    </FullPageDrawer>
  );
};

export default AddDocument;
